import { TextField } from '@material-ui/core';
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form';

import { BooleanInput } from 'components/standalone';
import { handleDecimalPlaces } from 'utils';

interface SwitchFieldProps<T extends FieldValues> {
  name: FieldPath<T>;
  control: Control<T>;
  label: string;
  disabled?: boolean;
  className?: string;
  /** Runs after the switch is turned off, so dependent fields can be cleared. */
  onUncheck?: () => void;
}

export const SwitchField = <T extends FieldValues>({
  name,
  control,
  label,
  disabled,
  className,
  onUncheck,
}: SwitchFieldProps<T>) => (
  <Controller
    name={name}
    control={control}
    render={({ field }) => (
      <BooleanInput
        label={label}
        checked={!!field.value}
        className={className}
        disabled={disabled}
        onChange={checked => {
          field.onChange(checked);
          if (!checked) onUncheck?.();
        }}
      />
    )}
  />
);

interface CostFieldProps<T extends FieldValues> {
  name: FieldPath<T>;
  control: Control<T>;
  label: string;
  disabled?: boolean;
  className?: string;
  error?: string;
  min?: number;
}

/** A cleared cost input; sent to the API as null so it overwrites the stored one. */
export const EMPTY_COST = '' as const;

export type CostValue = number | typeof EMPTY_COST;

/**
 * Two-decimal money input stored as a number (like the region/subdivision cost fields).
 * Storing a string would make React rewrite the DOM on every keystroke and drop
 * intermediate text such as "5.0", turning "5.05" into "55".
 */
export const CostField = <T extends FieldValues>({
  name,
  control,
  label,
  disabled,
  className,
  error,
  min = 0,
}: CostFieldProps<T>) => (
  <Controller
    name={name}
    control={control}
    render={({ field: { onChange, value, ...field } }) => (
      <TextField
        {...field}
        id={`${name}-input`}
        className={className}
        label={label}
        type="number"
        value={value}
        onChange={e => {
          const parsed = handleDecimalPlaces(e.target.value);
          onChange(parsed === '' ? EMPTY_COST : Number(parsed));
        }}
        inputProps={{ min, step: 0.01 }}
        error={!!error}
        helperText={error}
        margin="dense"
        variant="filled"
        disabled={disabled}
      />
    )}
  />
);
